import { ChatTransport } from "./ChatTransport";
import { streamJsonSseWithRetry } from "../../../../utils/SseStreamUtil";

const PRIVATE_EVENTS_PATH = (baseUrl, id) =>
  `${baseUrl}/private/ai/agent/session/${id}/events`;
const PRIVATE_STREAM_PATH = (baseUrl, id, after) =>
  `${baseUrl}/private/ai/agent/session/${id}/stream?after=${after}`;
const PUBLIC_EVENTS_PATH = (baseUrl, token) =>
  `${baseUrl}/private/ai/agent/public-chat/session/${token}/events`;
const PUBLIC_STREAM_PATH = (baseUrl, token, after) =>
  `${baseUrl}/private/ai/agent/public-chat/session/${token}/stream?after=${after}`;

export class DefaultChatTransport extends ChatTransport {
  constructor({ baseUrl, onEvent, onConnectionStateChange, onStreamDiagnostic, skipAuth = false }) {
    super();
    this._baseUrl = baseUrl;
    this._onEvent = onEvent;
    this._onConnectionStateChange = onConnectionStateChange;
    this._onStreamDiagnostic = onStreamDiagnostic;
    this._skipAuth = skipAuth;
    this._abortController = null;
    this._lastEventId = 0;
    this._connectId = null;
  }

  async connect(id, options = {}) {
    this.disconnect();
    this._connectId = id;
    this._lastEventId = 0;
    this._abortController = new AbortController();

    const url = this._skipAuth
      ? PUBLIC_EVENTS_PATH(this._baseUrl, id)
      : PRIVATE_EVENTS_PATH(this._baseUrl, id);
    return this._stream(url, options);
  }

  async resume(id, lastEventId) {
    this.disconnect();
    this._connectId = id;
    this._lastEventId = lastEventId || this._lastEventId;
    this._abortController = new AbortController();

    const url = this._skipAuth
      ? PUBLIC_STREAM_PATH(this._baseUrl, id, this._lastEventId)
      : PRIVATE_STREAM_PATH(this._baseUrl, id, this._lastEventId);
    return this._stream(url, {});
  }

  disconnect() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this._connectId = null;
  }

  get lastEventId() {
    return this._lastEventId;
  }

  async _stream(url, options) {
    const { shouldReconnect, maxRetries = 5 } = options;

    return streamJsonSseWithRetry({
      url,
      abortController: this._abortController,
      maxRetries,
      logLabel: this._skipAuth ? "PublicChatTransport SSE" : "ChatTransport SSE",
      skipAuth: this._skipAuth,
      onEvent: (eventName, data, eventId) => {
        if (eventId) {
          const parsed = Number(eventId);
          if (Number.isFinite(parsed) && parsed > this._lastEventId) {
            this._lastEventId = parsed;
          }
        }
        if (this._onEvent) {
          this._onEvent(eventName, data);
        }
      },
      shouldReconnect: () => {
        return typeof shouldReconnect === "function" ? shouldReconnect() : false;
      },
      onConnectionStateChange: (state) => {
        if (this._onConnectionStateChange) this._onConnectionStateChange(state);
      },
      onStreamDiagnostic: this._onStreamDiagnostic,
      getReconnectUrl: (lastEventId) => {
        if (lastEventId > 0 && this._connectId) {
          return this._skipAuth
            ? PUBLIC_STREAM_PATH(this._baseUrl, this._connectId, lastEventId)
            : PRIVATE_STREAM_PATH(this._baseUrl, this._connectId, lastEventId);
        }
        return null;
      },
    });
  }
}
